import React, { useState, useEffect, useContext } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faCalendarAlt,
  faClock,
  faBookmark,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../../contexts/UserContext";
import apiService from "../../../ApiService";
import "./StudyLogManager.css";

const StudyLogManager = () => {
  const { user } = useContext(UserContext);
  const userId = user?.userId;
  const [logs, setLogs] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSubject, setFilterSubject] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [formData, setFormData] = useState({
    subject_id: "",
    source_id: "",
    study_date: "",
    duration_minutes: "",
    notes: "",
  });

  useEffect(() => {
    fetchSubjects();
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (formData.subject_id) {
      fetchSourcesForSubject(formData.subject_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.subject_id]);

  const fetchSubjects = async () => {
    try {
      const response = await apiService.getSubjects(userId);
      setSubjects(response.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchSourcesForSubject = async (subjectId) => {
    try {
      const response = await apiService.getSources(userId, {
        subject_id: subjectId,
      });
      setSources(response.sources || []);
    } catch (error) {
      console.error("Error fetching sources:", error);
      setSources([]);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await apiService.getStudyLogs(userId, {
        subject_id: filterSubject || undefined,
      });
      setLogs(response.study_logs || []);
    } catch (error) {
      console.error("Error fetching study logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSubject]);

  const openCreateModal = () => {
    setEditingLog(null);
    const today = new Date().toISOString().split("T")[0];
    const firstSubjectId = subjects.length > 0 ? subjects[0].subject_id : "";
    setFormData({
      subject_id: firstSubjectId,
      source_id: "",
      study_date: today,
      duration_minutes: "",
      notes: "",
    });
    // Fetch sources for the first subject
    if (firstSubjectId) {
      fetchSourcesForSubject(firstSubjectId);
    }
    setShowModal(true);
  };

  const openEditModal = (log) => {
    setEditingLog(log);
    setFormData({
      subject_id: log.subject_id,
      source_id: log.source_id || "",
      study_date: log.study_date.split("T")[0],
      duration_minutes: log.duration_minutes,
      notes: log.notes || "",
    });
    // Fetch sources for the log's subject
    if (log.subject_id) {
      fetchSourcesForSubject(log.subject_id);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLog(null);
    setFormData({
      subject_id: "",
      source_id: "",
      study_date: "",
      duration_minutes: "",
      notes: "",
    });
    setSources([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Map fields for API
      const payload = {
        user_id: userId,
        subject_id: formData.subject_id,
        source_id: formData.source_id || null,
        study_date: formData.study_date,
        duration_minutes: parseInt(formData.duration_minutes),
        notes: formData.notes,
      };

      if (editingLog) {
        await apiService.updateStudyLog(userId, editingLog.log_id, payload);
      } else {
        await apiService.createStudyLog(userId, payload);
      }
      closeModal();
      fetchLogs();
    } catch (error) {
      console.error("Error saving study log:", error);
      alert("Failed to save study log. Please try again.");
    }
  };

  const handleDelete = async (logId, subjectName) => {
    if (
      window.confirm(
        `Are you sure you want to delete this study session for "${subjectName}"?`,
      )
    ) {
      try {
        await apiService.deleteStudyLog(userId, logId);
        fetchLogs();
      } catch (error) {
        console.error("Error deleting study log:", error);
        alert("Failed to delete study log. Please try again.");
      }
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getTotalDuration = () => {
    const filteredLogs = filterSubject
      ? logs.filter((log) => String(log.subject_id) === String(filterSubject))
      : logs;
    return filteredLogs.reduce((sum, log) => sum + log.duration_minutes, 0);
  };

  return (
    <div className="studylog-manager">
      {/* Header Actions */}
      <div className="manager-header">
        <div className="header-stats">
          <div className="stat-item">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <div>
              <span className="stat-label">Total Sessions</span>
              <span className="stat-value">{logs.length}</span>
            </div>
          </div>
          <div className="stat-item">
            <FontAwesomeIcon icon={faClock} />
            <div>
              <span className="stat-label">Total Time</span>
              <span className="stat-value">
                {formatDuration(getTotalDuration())}
              </span>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            className="filter-select"
          >
            <option value="">All Subjects</option>
            {subjects.map((subject) => (
              <option key={subject.subject_id} value={subject.subject_id}>
                {subject.subject_name}
              </option>
            ))}
          </select>
          <button onClick={openCreateModal} className="create-btn">
            <FontAwesomeIcon icon={faPlus} /> Log Session
          </button>
        </div>
      </div>

      {/* Study Logs List */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading study logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="empty-logs">
          <div className="empty-icon">⏱️</div>
          <h3>No study sessions logged</h3>
          <p>Start tracking your study time to monitor your progress!</p>
          <button onClick={openCreateModal} className="create-btn">
            <FontAwesomeIcon icon={faPlus} /> Log Your First Session
          </button>
        </div>
      ) : (
        <div className="logs-timeline">
          {logs.map((log) => (
            <div key={log.log_id} className="log-item">
              <div className="log-date-badge">
                <span className="day">
                  {new Date(log.study_date).getDate()}
                </span>
                <span className="month">
                  {new Date(log.study_date).toLocaleString("en-US", {
                    month: "short",
                  })}
                </span>
              </div>
              <div className="log-content">
                <div className="log-header">
                  <div className="log-info">
                    <h3>{log.subject_name}</h3>
                    {(log.source_title || log.source_name) && (
                      <p className="log-source">
                        <FontAwesomeIcon icon={faBookmark} />{" "}
                        {log.source_title || log.source_name}
                      </p>
                    )}
                  </div>
                  <div className="log-actions">
                    <button
                      onClick={() => openEditModal(log)}
                      className="action-btn edit-btn"
                      title="Edit"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      onClick={() => handleDelete(log.log_id, log.subject_name)}
                      className="action-btn delete-btn"
                      title="Delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
                <div className="log-meta">
                  <div className="meta-badge duration">
                    <FontAwesomeIcon icon={faClock} />
                    <span>{formatDuration(log.duration_minutes)}</span>
                  </div>
                  <span className="date-full">
                    {formatDate(log.study_date)}
                  </span>
                </div>
                {log.notes && <p className="log-notes">{log.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal &&
        ReactDOM.createPortal(
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {editingLog ? "Edit Study Session" : "Log Study Session"}
                </h2>
                <button onClick={closeModal} className="close-btn">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="subject_id">
                      Subject <span className="required">*</span>
                    </label>
                    <select
                      id="subject_id"
                      value={formData.subject_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subject_id: e.target.value,
                          source_id: "",
                        })
                      }
                      required
                    >
                      {subjects.length === 0 ? (
                        <option value="">No subjects available</option>
                      ) : (
                        subjects.map((subject) => (
                          <option
                            key={subject.subject_id}
                            value={subject.subject_id}
                          >
                            {subject.subject_name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="source_id">Source (Optional)</label>
                    <select
                      id="source_id"
                      value={formData.source_id}
                      onChange={(e) =>
                        setFormData({ ...formData, source_id: e.target.value })
                      }
                    >
                      <option value="">No specific source</option>
                      {sources.map((source) => (
                        <option key={source.source_id} value={source.source_id}>
                          {source.source_title || source.source_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="study_date">
                      Study Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      id="study_date"
                      value={formData.study_date}
                      onChange={(e) =>
                        setFormData({ ...formData, study_date: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="duration_minutes">
                      Duration (minutes) <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id="duration_minutes"
                      value={formData.duration_minutes}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          duration_minutes: e.target.value,
                        })
                      }
                      required
                      min="1"
                      max="1440"
                      placeholder="e.g., 60"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Session Notes</label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows="4"
                    placeholder="What did you study? Any key takeaways?"
                  ></textarea>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={subjects.length === 0}
                  >
                    {editingLog ? "Update" : "Log"} Session
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default StudyLogManager;
